import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Type, BarChart3, MessageSquare, Plus, Trash2, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { authFetch, getCurrentUser } from '../../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminContent() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  const navigate = useNavigate();

  const fetchContent = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/content`);
      setContent(await res.json());
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const user = getCurrentUser();
    if (!user.role || !['directeur', 'marketing'].includes(user.role)) {
      navigate('/admin'); return;
    }
    fetchContent();
  }, [fetchContent, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API_URL}/api/content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content),
      });
      if (!res.ok) throw new Error();
      toast.success('Contenu enregistre ! Les modifications sont visibles sur le site.');
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  const updateHero = (field, value) => setContent(prev => ({ ...prev, hero: { ...prev.hero, [field]: value } }));

  const updateStat = (index, field, value) => {
    setContent(prev => {
      const stats = [...prev.stats];
      stats[index] = { ...stats[index], [field]: value };
      return { ...prev, stats };
    });
  };

  const updateTestimonial = (index, field, value) => {
    setContent(prev => {
      const testimonials = [...prev.testimonials];
      testimonials[index] = { ...testimonials[index], [field]: value };
      return { ...prev, testimonials };
    });
  };

  const addTestimonial = () => setContent(prev => ({ ...prev, testimonials: [...(prev.testimonials || []), { name: '', role: '', text: '' }] }));
  const removeTestimonial = (index) => setContent(prev => ({ ...prev, testimonials: prev.testimonials.filter((_, i) => i !== index) }));

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Chargement...</div>;
  if (!content) return null;

  const tabs = [
    { id: 'hero', label: 'Hero & CTA', icon: Type },
    { id: 'stats', label: 'Chiffres cles', icon: BarChart3 },
    { id: 'testimonials', label: 'Temoignages', icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-content-page">
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard"><Button variant="ghost" size="sm"><ArrowLeft size={18} className="mr-2" /> Dashboard</Button></Link>
            <h1 className="text-xl font-bold text-[#003366]">Editeur de contenu</h1>
          </div>
          <div className="flex gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm"><Eye size={16} className="mr-2" /> Voir le site</Button></a>
            <Button onClick={handleSave} disabled={saving} className="bg-[#003366] hover:bg-[#002244] rounded-full" data-testid="save-content-btn">
              {saving ? <Loader2 className="mr-2 animate-spin" size={18} /> : <Save size={18} className="mr-2" />}Enregistrer
            </Button>
          </div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-[#003366] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-testid={`tab-${tab.id}`}>
              <tab.icon size={16} />{tab.label}
            </button>
          ))}
        </div>
        {activeTab === 'hero' && (
          <div className="bg-white rounded-xl border p-6 space-y-5" data-testid="hero-editor">
            <h2 className="font-bold text-[#003366] text-lg">Section Hero (page d'accueil)</h2>
            <div><Label>Titre principal</Label><Input value={content.hero?.title || ''} onChange={(e) => updateHero('title', e.target.value)} className="mt-1" data-testid="hero-title-input" /></div>
            <div><Label>Sous-titre / Description</Label><textarea value={content.hero?.subtitle || ''} onChange={(e) => updateHero('subtitle', e.target.value)} rows={3} className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[#003366]" data-testid="hero-subtitle-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Texte du bouton CTA</Label><Input value={content.hero?.cta_text || ''} onChange={(e) => updateHero('cta_text', e.target.value)} className="mt-1" data-testid="hero-cta-text" /></div>
              <div><Label>Lien du bouton CTA</Label><Input value={content.hero?.cta_link || ''} onChange={(e) => updateHero('cta_link', e.target.value)} className="mt-1" data-testid="hero-cta-link" /></div>
            </div>
          </div>
        )}
        {activeTab === 'stats' && (
          <div className="bg-white rounded-xl border p-6 space-y-5" data-testid="stats-editor">
            <h2 className="font-bold text-[#003366] text-lg">Chiffres cles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(content.stats || []).map((stat, i) => (
                <div key={`stat-${i}-${stat.label}`} className="border rounded-lg p-4 space-y-3">
                  <div><Label>Chiffre</Label><Input value={stat.number || ''} onChange={(e) => updateStat(i, 'number', e.target.value)} className="mt-1 text-lg font-bold" data-testid={`stat-number-${i}`} /></div>
                  <div><Label>Label</Label><Input value={stat.label || ''} onChange={(e) => updateStat(i, 'label', e.target.value)} className="mt-1" data-testid={`stat-label-${i}`} /></div>
                </div>
              ))}
            </div>
          </div>
        )}
        {activeTab === 'testimonials' && (
          <div className="bg-white rounded-xl border p-6 space-y-5" data-testid="testimonials-editor">
            <div className="flex items-center justify-between"><h2 className="font-bold text-[#003366] text-lg">Temoignages</h2><Button onClick={addTestimonial} variant="outline" size="sm" data-testid="add-testimonial-btn"><Plus size={16} className="mr-2" /> Ajouter</Button></div>
            {(content.testimonials || []).map((t, i) => (
              <div key={`testimonial-${i}-${t.name}`} className="border rounded-lg p-4 space-y-3 relative">
                <button onClick={() => removeTestimonial(i)} className="absolute top-3 right-3 text-red-400 hover:text-red-600" data-testid={`remove-testimonial-${i}`}><Trash2 size={16} /></button>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Nom</Label><Input value={t.name || ''} onChange={(e) => updateTestimonial(i, 'name', e.target.value)} className="mt-1" data-testid={`testimonial-name-${i}`} /></div>
                  <div><Label>Role / Promotion</Label><Input value={t.role || ''} onChange={(e) => updateTestimonial(i, 'role', e.target.value)} className="mt-1" data-testid={`testimonial-role-${i}`} /></div>
                </div>
                <div><Label>Temoignage</Label><textarea value={t.text || ''} onChange={(e) => updateTestimonial(i, 'text', e.target.value)} rows={2} className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[#003366]" data-testid={`testimonial-text-${i}`} /></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
